import {
  AlertTriangle,
  Eye,
  FileText,
  Loader2,
  Shield,
  Upload,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { useState, type Dispatch, type SetStateAction } from "react";
import type { DocumentUpload, KycType } from "@/interfaces/kyc";
import { uploadFiles } from "@/upload/upload";
import { useAppSelector } from "@/store/hooks";
import { getErrorMessage } from "@/utils/getErrorMessage";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

type KycLevel = 1 | 2 | 3;

interface Props {
  isLevelUploadAllowed: boolean;
  documents: Record<KycType, Record<KycLevel, DocumentUpload[]>>;
  setDocuments: Dispatch<
    SetStateAction<Record<KycType, Record<KycLevel, DocumentUpload[]>>>
  >;
  kycType: KycType;
  activeLevel: KycLevel;
}

const DocumentsContainer = ({
  isLevelUploadAllowed,
  documents,
  setDocuments,
  kycType,
  activeLevel,
}: Props) => {
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const [previewDocument, setPreviewDocument] = useState<DocumentUpload | null>(
    null,
  );
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const handleFileUpload = async (
    docId: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!isLevelUploadAllowed) return;

    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.warning("Error", {
        description: "File size must be less than 10MB",
      });
      return;
    }

    try {
      // 1. Set temporary uploading state
      setDocuments((prev) => ({
        ...prev,
        [kycType]: {
          ...prev[kycType],
          [activeLevel]: prev[kycType][activeLevel].map((doc) =>
            doc.id === docId ? { ...doc, status: "uploading" } : doc,
          ),
        },
      }));

      // 2. Upload to ImageKit
      const [result] = await uploadFiles({
        files: file,
        folder: `/kyc/${kycType.toLowerCase()}/level-${activeLevel}`,
        fileNamePrefix: `${userId}-${docId}`,
        onProgress: (progress) => {
          console.log(`Uploading ${docId}: ${progress.toFixed(0)}%`);
        },
      });

      // 3. Generate preview locally
      const preview = URL.createObjectURL(file);

      // 4. Update state with REAL URL
      setDocuments((prev) => ({
        ...prev,
        [kycType]: {
          ...prev[kycType],
          [activeLevel]: prev[kycType][activeLevel].map((doc) =>
            doc.id === docId
              ? {
                  ...doc,
                  file,
                  preview,
                  url: result.url,
                  status: "uploaded",
                }
              : doc,
          ),
        },
      }));

      toast.success("Uploaded!", {
        description: file.name,
      });
    } catch (error) {
      console.error(error);

      setDocuments((prev) => ({
        ...prev,
        [kycType]: {
          ...prev[kycType],
          [activeLevel]: prev[kycType][activeLevel].map((doc) =>
            doc.id === docId ? { ...doc, status: "pending" } : doc,
          ),
        },
      }));

      toast.error(getErrorMessage(error));
    }
  };

  console.log("DOCUMENTS", documents);

  const handleRemoveFile = (docId: string) => {
    if (!isLevelUploadAllowed) return;

    setDocuments((prev) => ({
      ...prev,
      [kycType]: {
        ...prev[kycType],
        [activeLevel]: prev[kycType][activeLevel].map((doc) =>
          doc.id === docId
            ? {
                ...doc,
                file: null,
                preview: null,
                url: null,
                status: "pending",
              }
            : doc,
        ),
      },
    }));
  };

  const handlePreview = (doc: DocumentUpload) => {
    setPreviewDocument(doc);
    setShowPreviewModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div className="text-sm">
            Files must be clear • Max 10MB • JPG, PNG, PDF supported
          </div>
        </div>
      </div>

      {documents[kycType][activeLevel].map((doc) => (
        <div key={doc.id} className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium">{doc?.name}</span>
            {doc?.status === "uploaded" && isLevelUploadAllowed && (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handlePreview(doc)}
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-500"
                  onClick={() => handleRemoveFile(doc.id)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {doc?.status === "uploading" ? (
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Uploading...</span>
            </div>
          ) : doc?.status === "pending" ? (
            isLevelUploadAllowed ? (
              <label className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary block">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => handleFileUpload(doc?.id, e)}
                />
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm">Click to upload or drag & drop</p>
              </label>
            ) : (
              <div className="border-2 border-dashed rounded-lg p-8 text-center opacity-50 cursor-not-allowed">
                <Shield className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm">Upload is locked for this level</p>
              </div>
            )
          ) : (
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              {doc?.preview && doc.file?.type.startsWith("image/") ? (
                <img
                  src={doc?.preview}
                  alt=""
                  className="w-12 h-12 object-cover rounded"
                />
              ) : (
                <FileText className="w-12 h-12 text-primary" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium truncate">
                  {doc?.file?.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {doc.file
                    ? `${(doc.file.size / (1024 * 1024)).toFixed(2)} MB`
                    : doc.metadata?.size
                      ? `${(doc.metadata.size / (1024 * 1024)).toFixed(2)} MB`
                      : "Uploaded"}
                </p>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{previewDocument?.name}</DialogTitle>
            <DialogDescription>Document Preview</DialogDescription>
          </DialogHeader>
          {previewDocument &&
            previewDocument.preview &&
            (previewDocument.file?.type.startsWith("image/") ? (
              <img
                src={previewDocument?.preview}
                alt="preview"
                className="w-full rounded-lg"
              />
            ) : (
              <div className="p-12 text-center bg-muted rounded-lg">
                <FileText className="mx-auto h-20 w-20 text-muted-foreground" />
                <p className="mt-4">PDF Preview Not Available</p>
              </div>
            ))}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsContainer;
